import { createTheme, alpha, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
    interface PaperPropsVariantOverrides {
        highlighted: true;
    }
}
declare module '@mui/material/styles' {
    interface ColorRange {
        50: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
    }

    interface PaletteColor extends ColorRange { }

    interface Palette {
        baseShadow: string;
    }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
    50: 'hsl(48, 100%, 96%)',
    100: 'hsl(48, 97%, 88%)',
    200: 'hsl(48, 96%, 78%)',
    300: 'hsl(47, 95%, 68%)',
    400: 'hsl(45, 93%, 58%)',
    500: 'hsl(43, 96%, 54%)',
    600: 'hsl(41, 98%, 46%)',
    700: 'hsl(38, 92%, 38%)',
    800: 'hsl(34, 86%, 30%)',
    900: 'hsl(30, 78%, 22%)',
};

export const gray = {
    50: 'hsl(220, 25%, 98%)',
    100: 'hsl(220, 22%, 95%)',
    200: 'hsl(220, 18%, 90%)',
    300: 'hsl(220, 16%, 82%)',
    400: 'hsl(220, 14%, 68%)',
    500: 'hsl(220, 12%, 52%)',
    600: 'hsl(220, 12%, 38%)',
    700: 'hsl(220, 16%, 26%)',
    800: 'hsl(220, 20%, 15%)',
    900: 'hsl(220, 24%, 8%)',
};

export const green = {
    50: 'hsl(120, 80%, 98%)',
    100: 'hsl(120, 75%, 94%)',
    200: 'hsl(120, 75%, 87%)',
    300: 'hsl(120, 61%, 77%)',
    400: 'hsl(120, 44%, 53%)',
    500: 'hsl(120, 59%, 30%)',
    600: 'hsl(120, 70%, 25%)',
    700: 'hsl(120, 75%, 16%)',
    800: 'hsl(120, 84%, 10%)',
    900: 'hsl(120, 87%, 6%)',
};

export const orange = {
    50: 'hsl(45, 100%, 97%)',
    100: 'hsl(45, 92%, 90%)',
    200: 'hsl(45, 94%, 80%)',
    300: 'hsl(45, 90%, 65%)',
    400: 'hsl(45, 90%, 40%)',
    500: 'hsl(45, 90%, 35%)',
    600: 'hsl(45, 91%, 25%)',
    700: 'hsl(45, 94%, 20%)',
    800: 'hsl(45, 95%, 16%)',
    900: 'hsl(45, 93%, 12%)',
};

export const red = {
    50: 'hsl(0, 100%, 97%)',
    100: 'hsl(0, 92%, 90%)',
    200: 'hsl(0, 94%, 80%)',
    300: 'hsl(0, 90%, 65%)',
    400: 'hsl(0, 90%, 40%)',
    500: 'hsl(0, 90%, 30%)',
    600: 'hsl(0, 91%, 25%)',
    700: 'hsl(0, 94%, 18%)',
    800: 'hsl(0, 95%, 12%)',
    900: 'hsl(0, 93%, 6%)',
};

export const getDesignTokens = (mode: PaletteMode) => {
    customShadows[1] =
        mode === 'dark'
            ? '0px 2px 8px hsla(220, 30%, 5%, 0.55), 0px 16px 32px -12px hsla(220, 25%, 10%, 0.75)'
            : '0px 1px 2px hsla(220, 20%, 20%, 0.08), 0px 12px 24px -16px hsla(220, 20%, 20%, 0.18)';

    return {
        palette: {
            mode,
            primary: {
                light: brand[200],
                main: brand[400],
                dark: brand[700],
                contrastText: brand[50],
                ...(mode === 'dark' && {
                    contrastText: brand[50],
                    light: brand[300],
                    main: brand[400],
                    dark: brand[700],
                }),
            },
            info: {
                light: brand[100],
                main: brand[300],
                dark: brand[600],
                contrastText: gray[50],
                ...(mode === 'dark' && {
                    contrastText: brand[300],
                    light: brand[500],
                    main: brand[700],
                    dark: brand[900],
                }),
            },
            warning: {
                light: orange[300],
                main: orange[400],
                dark: orange[800],
                ...(mode === 'dark' && {
                    light: orange[400],
                    main: orange[500],
                    dark: orange[700],
                }),
            },
            error: {
                light: red[300],
                main: red[400],
                dark: red[800],
                ...(mode === 'dark' && {
                    light: red[400],
                    main: red[500],
                    dark: red[700],
                }),
            },
            success: {
                light: green[300],
                main: green[400],
                dark: green[800],
                ...(mode === 'dark' && {
                    light: green[400],
                    main: green[500],
                    dark: green[700],
                }),
            },
            grey: {
                ...gray,
            },
            divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[300], 0.4),
            background: {
                default: 'hsl(220, 25%, 98%)',
                paper: 'hsl(0, 0%, 100%)',
                ...(mode === 'dark' && { default: gray[900], paper: 'hsl(220, 20%, 12%)' }),
            },
            text: {
                primary: gray[800],
                secondary: gray[600],
                warning: orange[400],
                ...(mode === 'dark' && { primary: 'hsl(0, 0%, 100%)', secondary: gray[400] }),
            },
            action: {
                hover: alpha(gray[200], 0.35),
                selected: `${alpha(gray[200], 0.45)}`,
                ...(mode === 'dark' && {
                    hover: alpha(gray[600], 0.2),
                    selected: alpha(gray[600], 0.3),
                }),
            },
        },
        typography: {
            fontFamily: "'Merriweather', Georgia, 'Times New Roman', serif",
            h1: {
                fontSize: defaultTheme.typography.pxToRem(44),
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: -0.4,
            },
            h2: {
                fontSize: defaultTheme.typography.pxToRem(34),
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: -0.3,
            },
            h3: {
                fontSize: defaultTheme.typography.pxToRem(28),
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: -0.2,
            },
            h4: {
                fontSize: defaultTheme.typography.pxToRem(22),
                fontWeight: 700,
                lineHeight: 1.35,
                letterSpacing: -0.15,
            },
            h5: {
                fontSize: defaultTheme.typography.pxToRem(18),
                fontWeight: 700,
                letterSpacing: -0.1,
            },
            h6: {
                fontSize: defaultTheme.typography.pxToRem(16),
                fontWeight: 700,
                letterSpacing: -0.05,
            },
            subtitle1: {
                fontSize: defaultTheme.typography.pxToRem(16),
            },
            subtitle2: {
                fontSize: defaultTheme.typography.pxToRem(13),
                fontWeight: 400,
            },
            body1: {
                fontSize: defaultTheme.typography.pxToRem(15),
                lineHeight: 1.5,
            },
            body2: {
                fontSize: defaultTheme.typography.pxToRem(14),
                fontWeight: 400,
                lineHeight: 1.5,
            },
            caption: {
                fontSize: defaultTheme.typography.pxToRem(12),
                fontWeight: 400,
            },
        },
        shape: {
            borderRadius: 8,
        },
        shadows: customShadows,
    };
};

export const colorSchemes = {
    light: {
        palette: {
            primary: {
                light: brand[300],
                main: brand[400],
                dark: brand[500],
                contrastText: brand[50],
            },
            info: {
                light: brand[100],
                main: brand[300],
                dark: brand[600],
                contrastText: gray[50],
            },
            warning: {
                light: orange[200],
                main: orange[300],
                dark: orange[700],
            },
            error: {
                light: red[300],
                main: red[400],
                dark: red[800],
            },
            success: {
                light: green[300],
                main: green[400],
                dark: green[800],
            },
            grey: {
                ...gray,
            },
            divider: alpha(gray[300], 0.4),
            background: {
                default: 'hsl(220, 25%, 98%)',
                paper: 'hsl(0, 0%, 100%)',
            },
            text: {
                primary: gray[800],
                secondary: gray[600],
                warning: orange[400],
            },
            action: {
                hover: alpha(gray[200], 0.35),
                selected: `${alpha(gray[200], 0.45)}`,
            },
            baseShadow:
                '0px 1px 2px hsla(220, 20%, 20%, 0.08), 0px 12px 24px -16px hsla(220, 20%, 20%, 0.18)',
        },
    },
    dark: {
        palette: {
            primary: {
                contrastText: brand[50],
                light: brand[300],
                main: brand[400],
                dark: brand[700],
            },
            info: {
                contrastText: brand[300],
                light: brand[500],
                main: brand[700],
                dark: brand[900],
            },
            warning: {
                light: orange[400],
                main: orange[500],
                dark: orange[700],
            },
            error: {
                light: red[400],
                main: red[500],
                dark: red[700],
            },
            success: {
                light: green[400],
                main: green[500],
                dark: green[700],
            },
            grey: {
                ...gray,
            },
            divider: alpha(gray[700], 0.6),
            background: {
                default: 'blue',
                paper: 'hsl(220, 20%, 12%)',
            },
            text: {
                primary: 'hsl(0, 0%, 100%)',
                secondary: gray[400],
            },
            action: {
                hover: alpha(gray[600], 0.2),
                selected: alpha(gray[600], 0.3),
            },
            baseShadow:
                '0px 2px 8px hsla(220, 30%, 5%, 0.55), 0px 16px 32px -12px hsla(220, 25%, 10%, 0.75)',
        },
    },
};

export const typography = {
    fontFamily: "'Merriweather', Georgia, 'Times New Roman', serif",
    h1: {
        fontSize: defaultTheme.typography.pxToRem(44),
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: -0.4,
    },
    h2: {
        fontSize: defaultTheme.typography.pxToRem(34),
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: -0.3,
    },
    h3: {
        fontSize: defaultTheme.typography.pxToRem(28),
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: -0.2,
    },
    h4: {
        fontSize: defaultTheme.typography.pxToRem(22),
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: -0.15,
    },
    h5: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 700,
        letterSpacing: -0.1,
    },
    h6: {
        fontSize: defaultTheme.typography.pxToRem(16),
        fontWeight: 700,
        letterSpacing: -0.05,
    },
    subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(16),
    },
    subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(13),
        fontWeight: 400,
    },
    body1: {
        fontSize: defaultTheme.typography.pxToRem(15),
        lineHeight: 1.5,
    },
    body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
        lineHeight: 1.5,
    },
    caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
    },
};

export const shape = {
    borderRadius: 8,
};

// @ts-ignore
const defaultShadows: Shadows = [
    'none',
    'var(--template-palette-baseShadow)',
    ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;